import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  IconButton,
  Input,
  Modal,
  Select,
  type SelectOption,
} from '../../../components';
import { ApiError, apiErrorMessage, apiRequest } from '../../../lib/api';
import { usePermissions } from '../../../lib/permissions';
import type { ListResponse } from '../../../lib/useCrud';
import type { UserOption } from '../types';

type FollowupRow = {
  id: string;
  followup_type: string | null;
  description: string | null;
  status: 'pending' | 'in_progress' | 'done' | 'cancelled';
  due_date: string | null;
  completed_at: string | null;
  assigned_user_id: string | null;
  assignee: { id: string; full_name: string } | null;
  created_at: string;
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function FollowupsTab({
  leadId,
  users,
}: {
  leadId: string;
  users: UserOption[];
}) {
  const qc = useQueryClient();
  // Mirror the follow-ups workspace: read-only users can't create/complete and
  // deletes are admin-only. The backend enforces both — this keeps the UI honest.
  const { canWrite, isAdmin } = usePermissions();
  const [composing, setComposing] = useState(false);

  const list = useQuery({
    queryKey: ['lead-followups', leadId],
    queryFn: () =>
      apiRequest<ListResponse<FollowupRow>>(`/followups?lead_id=${leadId}&limit=100`),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lead-followups', leadId] });

  const createMut = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<{ data: FollowupRow }>('/followups', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const completeMut = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: FollowupRow }>(`/followups/${id}/complete`, { method: 'POST' }),
    onSuccess: invalidate,
    onError: (err) => alert(apiErrorMessage(err, 'Could not mark complete')),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/followups/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (err) => alert(apiErrorMessage(err, 'Delete failed')),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" icon="plus" onClick={() => setComposing(true)}>
            New follow-up
          </Button>
        </div>
      )}

      {list.isLoading && <div className="card card-body muted">Loading…</div>}
      {list.error && (
        <div className="alert">
          {list.error instanceof Error ? list.error.message : 'Failed to load'}
        </div>
      )}
      {list.data && list.data.data.length === 0 && (
        <div className="card card-body muted">No follow-ups.</div>
      )}
      {list.data && list.data.data.length > 0 && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Assignee</th>
                <th>Due</th>
                <th>Status</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {list.data.data.map((f) => {
                const overdue =
                  f.due_date &&
                  ['pending', 'in_progress'].includes(f.status) &&
                  new Date(f.due_date).getTime() < Date.now();
                return (
                  <tr key={f.id}>
                    <td>{f.followup_type ?? <span className="muted">—</span>}</td>
                    <td>{f.description ?? <span className="muted">—</span>}</td>
                    <td>{f.assignee?.full_name ?? <span className="muted">—</span>}</td>
                    <td
                      className={overdue ? 'alert' : ''}
                      style={overdue ? { padding: 8 } : undefined}
                    >
                      {f.due_date ? new Date(f.due_date).toLocaleString() : '—'}
                    </td>
                    <td>
                      <span className={`badge ${f.status === 'done' ? 'green' : 'gray'}`}>
                        {f.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {canWrite && f.status !== 'done' && f.status !== 'cancelled' && (
                          <IconButton
                            name="check"
                            label="Mark complete"
                            onClick={() => completeMut.mutate(f.id)}
                          />
                        )}
                        {isAdmin && (
                          <IconButton
                            name="trash"
                            label="Delete"
                            onClick={() => deleteMut.mutate(f.id)}
                          />
                        )}
                        {!canWrite && (
                          <span className="muted" style={{ fontSize: 12 }}>
                            View only
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {composing && (
        <FollowupCreateModal
          users={users}
          onClose={() => setComposing(false)}
          onSubmit={async (payload) => {
            await createMut.mutateAsync({ lead_id: leadId, ...payload });
            setComposing(false);
          }}
        />
      )}
    </div>
  );
}

function FollowupCreateModal({
  users,
  onClose,
  onSubmit,
}: {
  users: UserOption[];
  onClose: () => void;
  onSubmit: (payload: {
    followup_type: string | null;
    description: string | null;
    status: string;
    due_date: string | null;
    assigned_user_id: string | null;
  }) => Promise<void>;
}) {
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('pending');
  const [due, setDue] = useState('');
  const [assignee, setAssignee] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        followup_type: type.trim() || null,
        description: description.trim() || null,
        status,
        due_date: fromDatetimeLocal(due),
        assigned_user_id: assignee || null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  const userOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  return (
    <Modal open onClose={onClose} title="New follow-up" width={480}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            id="fu-type"
            label="Type"
            value={type}
            onChange={setType}
            placeholder="call, email, meeting…"
          />
          <div>
            <label className="field-label" htmlFor="fu-desc">
              Description
            </label>
            <textarea
              id="fu-desc"
              className="input"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="fu-due">
                Due
              </label>
              <input
                id="fu-due"
                className="input"
                type="datetime-local"
                value={due || toDatetimeLocal(null)}
                onChange={(e) => setDue(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="fu-status"
                label="Status"
                value={status}
                options={STATUS_OPTIONS}
                onChange={setStatus}
              />
            </div>
          </div>
          <Select
            id="fu-assignee"
            label="Assign to"
            placeholder="— unassigned —"
            value={assignee}
            options={userOpts}
            onChange={setAssignee}
          />
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
